<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';
import { Icon } from '~ds/components/ui/icon';
import { Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { Motion } from 'motion-v';

import { useCollectionDevTools } from '~/composables/unified/DevPanel/useCollectionDevTools';
import { useDevPanelDrag } from '~/composables/unified/DevPanel/useDevPanelDrag';
import { useStoryDevTools } from '~/composables/unified/useStoryDevTools';

defineProps<{
  isLoading?: boolean;
}>();

const { hasActiveStory } = useStoryDevTools();
const { activeCollectionSlug } = useCollectionDevTools();

const panelStateCookie = useClusterCookie<boolean>('unifiedDevPanelOpen', {
  default: () => false,
  maxAge: 60 * 60 * 24 * 365,
});

const isPanelOpen = ref(panelStateCookie.value);

watch(isPanelOpen, (newValue) => {
  panelStateCookie.value = newValue;
});

const { x, y, dragControls, isDragging, hasDragged, isTopCorner, isRightCorner, onDragStart, onDrag, onDragEnd } = useDevPanelDrag();

const onButtonClick = () => {
  if (hasDragged.value) {
    return;
  }
  isPanelOpen.value = !isPanelOpen.value;
};
</script>

<template>
  <!-- Teleported to Nuxt's #teleports target so the panel escapes the dashboard's
       `:inert` content wrapper and stays interactive while the navigation panel is
       open. #teleports is used (not body) because it is Nuxt's only SSR-supported
       teleport target. -->
  <Teleport to="#teleports">
    <!-- z-70 + pointer-events-auto keep the panel visible and clickable above the
         fullscreen activity credenza (fixed z-60 modal dialog, which disables pointer
         events outside its own content). -->
    <Motion
      class="pointer-events-auto fixed left-0 top-0 z-70 font-mono text-xs"
      drag
      :drag-controls="dragControls"
      :drag-listener="false"
      :drag-elastic="0"
      :drag-momentum="false"
      :style="{ x, y }"
      @drag="onDrag"
      @drag-end="onDragEnd"
    >
      <div
        :style="{
          transform: `translate(${isRightCorner ? '-100%' : '0'}, ${isTopCorner ? '0' : '-100%'})`,
        }"
      >
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          :enter-from-class="`opacity-0 scale-95 ${isTopCorner ? '-translate-y-2' : 'translate-y-2'}`"
          enter-to-class="opacity-100 translate-y-0 scale-100"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0 scale-100"
          :leave-to-class="`opacity-0 scale-95 ${isTopCorner ? '-translate-y-2' : 'translate-y-2'}`"
        >
          <div
            v-if="isPanelOpen"
            class="absolute w-80 rounded-lg border bg-white text-muted-foreground shadow-xl"
            :class="[
              isTopCorner ? 'top-full mt-2' : 'bottom-full mb-2',
              isRightCorner ? 'right-0' : 'left-0',
            ]"
          >
            <div class="flex items-center justify-between px-3 py-2">
              <div class="flex items-center gap-2">
                <Text
                  as="span"
                  size="sm"
                  weight="semibold"
                  class="uppercase tracking-wider text-muted-foreground"
                >
                  Dev Panel
                </Text>
                <KUICoreLoaderAnyway
                  v-if="isLoading"
                  size="2xs"
                />
              </div>
              <Button
                variant="link"
                size="sm"
                class="!text-muted-foreground hover:!text-muted-foreground"
                type="button"
                @click="isPanelOpen = false"
              >
                <Icon :name="ICONS.cancel" />
              </Button>
            </div>

            <div class="border-t">
              <UnifiedDevPanelSection title="Chat" default-open>
                <UnifiedDevPanelChat />
              </UnifiedDevPanelSection>
            </div>

            <div class="border-t">
              <UnifiedDevPanelSection title="Coach">
                <UnifiedDevPanelCoachActions />
              </UnifiedDevPanelSection>
            </div>

            <div class="border-t">
              <UnifiedDevPanelSection title="Entry lanes">
                <UnifiedDevPanelEntryLanes />
              </UnifiedDevPanelSection>
            </div>

            <div class="border-t">
              <UnifiedDevPanelSection title="Economy">
                <UnifiedDevPanelConversationState />
              </UnifiedDevPanelSection>
            </div>

            <div class="border-t">
              <UnifiedDevPanelSection title="Sockets">
                <ClientOnly>
                  <UnifiedDevPanelWebSocket />
                </ClientOnly>
              </UnifiedDevPanelSection>
            </div>

            <div v-if="hasActiveStory" class="border-t">
              <UnifiedDevPanelSection title="Story" default-open>
                <UnifiedDevPanelStoryActions />
              </UnifiedDevPanelSection>
            </div>

            <div v-if="activeCollectionSlug" class="border-t">
              <UnifiedDevPanelSection title="Collection" default-open>
                <UnifiedDevPanelCollectionActions :collection-slug="activeCollectionSlug" />
              </UnifiedDevPanelSection>
            </div>
          </div>
        </Transition>
        <button
          class="flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg transition-all border-brand-purple text-foreground hover:bg-brand-purple-subtle"
          :class="{ 'bg-brand-purple-subtle': isPanelOpen, 'cursor-grabbing': isDragging, 'cursor-grab': !isDragging }"
          @pointerdown="onDragStart"
          @click="onButtonClick"
        >
          <KUICoreLoaderAnyway
            v-if="isLoading"
            size="sm"
          />
          <template v-else>
            <Icon :name="ICONS.code" size="lg" />
            <span class="text-xs font-semibold uppercase tracking-wider">Dev</span>
          </template>
        </button>
      </div>
    </Motion>
  </Teleport>
</template>
